// src/pages/UserManagementPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubdomain } from '../contexts/SubdomainContext';
import Header from '../components/Header';
import UserInvitation from '../components/UserInvitation';
import UserList from '../components/UserList';
import UserPermissionInitializer from '../components/UserPermissionInitializer';
import AccountDebugger from '../components/AccountDebugger';
import {
    getAccountUsers,
    getUserPermissions,
    isAccountOwner,
    hasPermission
} from '../services/userManagementService';
import { PERMISSIONS } from '../types/permissions';
import '../styles/pages/UserManagementPage.scss';

const UserManagementPage = () => {
    const { accountId } = useParams();
    const { currentUser } = useAuth();
    const { subdomain, hasSubdomain } = useSubdomain();
    const [users, setUsers] = useState([]);
    const [userPermissions, setUserPermissions] = useState([]);
    const [isOwner, setIsOwner] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('account');

    useEffect(() => {
        loadData();
    }, [accountId, currentUser]);

    const loadData = async () => {
        if (!currentUser || !accountId) return;

        try {
            setLoading(true);

            // ユーザーの権限を取得
            const permissions = await getUserPermissions(accountId, currentUser.uid);
            setUserPermissions(permissions || []);

            // アカウントオーナーかチェック
            const ownerStatus = await isAccountOwner(accountId, currentUser.uid);
            setIsOwner(ownerStatus);

            // ユーザー管理権限をチェック
            if (ownerStatus || hasPermission(permissions, PERMISSIONS.USERS_VIEW)) {
                const usersData = await getAccountUsers(accountId);
                setUsers(usersData);
            }
        } catch (error) {
            console.error('データの読み込みに失敗:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUserInvited = () => {
        // ユーザー招待後、一覧を再読み込み
        loadData();
    };

    const handleUserUpdated = () => {
        // ユーザー更新後、一覧を再読み込み
        loadData();
    };

    // 権限チェック
    const canViewUsers = isOwner || hasPermission(userPermissions, PERMISSIONS.USERS_VIEW);
    const canInviteUsers = isOwner || hasPermission(userPermissions, PERMISSIONS.USERS_INVITE);

    if (loading) {
        return (
            <div>
                <Header />
                <main className="user-management-page">
                    <div className="container">
                        <div className="loading">読み込み中...</div>
                    </div>
                </main>
            </div>
        );
    }

    if (!canViewUsers) {
        return (
            <div>
                <Header />
                <main className="user-management-page">
                    <div className="container">
                        <div className="access-denied">
                            <h2>アクセス権限がありません</h2>
                            <p>このページを表示する権限がありません。</p>

                            {/* 初回設定用のコンポーネント */}
                            <UserPermissionInitializer accountId={accountId} />

                            {/* 開発環境でのデバッグ情報 */}
                            <AccountDebugger accountId={accountId} />
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div>
            <Header />
            <main className="user-management-page">
                <div className="container">
                    <div className="user-management-layout">
                        {/* 左側のナビゲーション */}
                        <div className="sidebar">
                            <nav className="nav">
                                <button
                                    className={`nav-button ${activeTab === 'account' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('account')}
                                >
                                    アカウント情報
                                </button>
                                <button
                                    className={`nav-button ${activeTab === 'users' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('users')}
                                >
                                    ユーザー一覧
                                </button>
                                {canInviteUsers && (
                                    <button
                                        className={`nav-button ${activeTab === 'invite' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('invite')}
                                    >
                                        ユーザー追加
                                    </button>
                                )}
                            </nav>
                        </div>

                        {/* 右側のコンテンツ */}
                        <div className="content">
                            {activeTab === 'account' && (
                                <div className="tab-content">
                                    <h2>アカウント情報</h2>
                                    <div className="info-card">
                                        <div className="info-item">
                                            <label>WebサイトURL:</label>
                                            <span>
                                                {hasSubdomain ? (
                                                    <a
                                                        href={`https://${subdomain.subdomain}.suisui.life`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="subdomain-link"
                                                    >
                                                        {subdomain.subdomain}.suisui.life
                                                    </a>
                                                ) : (
                                                    <span className="no-subdomain">未設定（サブドメイン設定が必要）</span>
                                                )}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <label>アカウントID:</label>
                                            <span className="account-id">{accountId}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'users' && (
                                <div className="tab-content">
                                    <h2>ユーザー一覧</h2>
                                    <UserList
                                        users={users}
                                        currentUser={currentUser}
                                        accountId={accountId}
                                        isOwner={isOwner}
                                        userPermissions={userPermissions}
                                        onUserUpdated={handleUserUpdated}
                                    />
                                </div>
                            )}

                            {activeTab === 'invite' && canInviteUsers && (
                                <div className="tab-content">
                                    <h2>ユーザー追加</h2>
                                    <UserInvitation
                                        accountId={accountId}
                                        currentUser={currentUser}
                                        onUserInvited={handleUserInvited}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default UserManagementPage;
